import { useEffect, useState } from 'react';
import { Button } from '../../../components';
import { TableColumnsType, Tooltip } from 'antd';
import { useMutation, useQuery } from '@apollo/client';

import {
  CREATE_SUPPLIER,
  DELETE_SUPPLIER,
  EDIT_SUPPLIER,
  FETCH_SUPPLIERS,
} from '../queries/suppliers';

import TitleBar from '../../../components/TitleBar';
import { Footer } from '../../../components/common/Footer';
import { CraftBox } from '../../../components/assets/CraftBox';
import UploadFile from '../../../components/assets/UploadFile';
import TableV3, {
  ModifyVariablesArgs,
} from '../../../components/generic/Table/TableV3';

import MaterialsForm from './Materials';
import BCPFile from './BCPFile';
import { SupplierType, SupplierTypeForm } from './types';
import { formFields } from './forms/fomfields';

import {
  cleanUpObject,
  formatSupplierData,
  removeEmptyCommas,
  removePlusSignSuffix,
  tableSortingMethods,
} from '../../../utils';
import { startCase } from 'lodash';

import './Suppliers.css';
import { useUserContext } from '../../../context/PermissionsContext';
import QueryErrorBoundary from '../../../components/error-handling';
import TitleUpdater from '../../../components/common/TitleUpdater';
import { CollapsibleText } from '../../../components/generic/TextTruncation';

export default function Suppliers() {
  const [isMaterialsModalOpen, setIsMaterialsModalOpen] = useState(false);
  const [isBCPModalOpen, setIsBCPModalOpen] = useState(false);
  const [activeRecordId, setActiveRecordId] = useState<string | null>(null);
  const [acceptedFile, setAcceptedFile] = useState<File | null>(null);
  const [currentFile, setCurrentFile] = useState<File | null>(null);

  const { isAdmin, canEdit } = useUserContext();

  const { data, loading, error, refetch } = useQuery<{
    findAllSupplier: SupplierType[];
  }>(FETCH_SUPPLIERS);

  const formattedData: SupplierType[] = formatSupplierData(
    data?.findAllSupplier ?? [],
  );

  const [createSupplier, { loading: createSupplierLoading }] = useMutation(
    CREATE_SUPPLIER,
    {
      refetchQueries: ['GetSuppliers'],
    },
  );

  const [editSupplier, { loading: editSupplierLoading }] = useMutation(
    EDIT_SUPPLIER,
    {
      refetchQueries: ['GetSuppliers'],
    },
  );

  const [deleteSupplier, { loading: deleteSupplierLoading }] = useMutation(
    DELETE_SUPPLIER,
    {
      refetchQueries: ['GetSuppliers'],
    },
  );

  const loadingStates =
    loading ||
    createSupplierLoading ||
    editSupplierLoading ||
    deleteSupplierLoading;

  const recordToEdit: SupplierTypeForm | undefined =
    data?.findAllSupplier?.find(
      (record) => record.uuid === activeRecordId,
    ) as unknown as SupplierTypeForm;

  const updateRecordId = (recordId: string | null) =>
    setActiveRecordId(recordId);

  const handleShowMaterialsForm = (recordId: string) => {
    updateRecordId(recordId);
    setIsMaterialsModalOpen(true);
  };

  const handleCloseMaterialsForm = () => {
    setIsMaterialsModalOpen(false);
    updateRecordId(null);
  };

  const handleShowBCPModal = (recordId: string) => {
    updateRecordId(recordId);
    setIsBCPModalOpen(true);
  };

  const handleCloseBCPModal = () => {
    setIsBCPModalOpen(false);
    updateRecordId(null);
    setAcceptedFile(null);
  };

  const updateAcceptedFile = (file: File | null) => setAcceptedFile(file);

  const updateCurrentFile = (file: File | null) => setCurrentFile(file);

  const columns: TableColumnsType<SupplierType> = [
    {
      title: 'Supplier',
      dataIndex: 'supplierName',
      key: 'supplierName',
      width: 180,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.supplierName, b.supplierName),
      render: (text) => <span className="custom-table--column">{text}</span>,
    },
    {
      title: 'Address',
      dataIndex: 'supplierAddress',
      key: 'supplierAddress',
      width: 220,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.supplierAddress, b.supplierAddress),
      render: (text) => (
        <span className="custom-table--column">
          {removeEmptyCommas(text) || (
            <div className="placeholder-text text-sm">Address Not Provided</div>
          )}
        </span>
      ),
    },
    {
      title: (
        <div>
          Replacement Time
          <br />
          (months)
        </div>
      ),
      dataIndex: 'supplierRecoveryTime',
      key: 'supplierRecoveryTime',
      width: 80,
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.supplierRecoveryTime,
          b.supplierRecoveryTime,
        ),
      render: (text) =>
        text !== undefined && text !== null ? (
          <div className="custom-table--column">{text}</div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      title: 'Source Status',
      dataIndex: 'sourcingStatus',
      key: 'sourcingStatus',
      align: 'center',
      width: 120,
      sorter: (a, b) =>
        tableSortingMethods.string(a.sourcingStatus, b.sourcingStatus),
      render: (text) => <span className="custom-table--column">{text}</span>,
    },
    {
      title: 'Available Alternatives',
      dataIndex: 'availableAlternatives',
      key: 'availableAlternatives',
      width: 70,
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.number(
          Number(removePlusSignSuffix(a.availableAlternatives)),
          Number(removePlusSignSuffix(b.availableAlternatives)),
        ),
      render: (text) => <span className="custom-table--column">{text}</span>,
    },
    {
      title: 'Qualified Alternatives',
      dataIndex: 'qualifiedAlternatives',
      key: 'qualifiedAlternatives',
      width: 70,
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.number(
          Number(removePlusSignSuffix(a.qualifiedAlternatives)),
          Number(removePlusSignSuffix(b.qualifiedAlternatives)),
        ),
      render: (text) => <span className="custom-table--column">{text}</span>,
    },
    {
      title: 'Supplier BCP Added',
      dataIndex: 'bcpUploaded',
      key: 'bcpUploaded',
      width: 80,
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.boolean(a.bcpUploaded, b.bcpUploaded),
      render: (text) => (
        <span className="custom-table--column">{text ? 'Yes' : 'No'}</span>
      ),
    },
    {
      title: 'Comment',
      dataIndex: 'comment',
      key: 'comment',
      width: 250,
      align: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.comment, b.comment),
      render: (text: string) => <CollapsibleText text={text} />,
    },
  ];

  const extendedActions = (record: any) =>
    isAdmin || canEdit ? (
      <>
        <Tooltip title="Upload BCP">
          <div>
            <Button
              className="border border-blue bg-white"
              type="edit"
              onClick={() => handleShowBCPModal(record.uuid)}
            >
              <UploadFile />
            </Button>
          </div>
        </Tooltip>
        <Tooltip title="Add/Edit Materials">
          <div>
            <Button
              className="border border-blue bg-white"
              type="edit"
              onClick={() => handleShowMaterialsForm(record.uuid)}
            >
              <CraftBox />
            </Button>
          </div>
        </Tooltip>
      </>
    ) : null;

  useEffect(() => {
    refetch();
  }, [acceptedFile, currentFile]);

  const suppliersFormatter = (data: SupplierType) => {
    const approxAnualSpend = parseFloat(data?.approxAnualSpend?.toString());
    const supplierRecoveryTime = parseInt(
      data?.supplierRecoveryTime?.toString(),
    );
    const timeToReplaceSupplier = parseInt(
      data?.timeToReplaceSupplier?.toString(),
    );
    const availableAlternatives = parseInt(data?.availableAlternatives);
    const qualifiedAlternatives = parseInt(data?.qualifiedAlternatives);

    const customData = {
      supplierName: data.supplierName,
      street: data.street,
      city: data.city,
      state: data.state,
      zipCode: data?.zipCode.toString(),
      country: data.country,
      sourcingStatus: startCase(data?.sourcingStatus ?? '')
        .split(' ')
        .join(''),
      approxAnualSpend: approxAnualSpend || 0,
      supplierRecoveryTime,
      timeToReplaceSupplier: timeToReplaceSupplier || 0,
      availableAlternatives: availableAlternatives || 0,
      qualifiedAlternatives: qualifiedAlternatives || 0,
      comment: data.comment,
    };

    const cleanedData = cleanUpObject(customData);

    // Preserve comment field even if it's empty string
    // This ensures that clearing the comment field will be saved
    if (data.hasOwnProperty('comment')) {
      cleanedData.comment = data.comment || '';
    }

    return cleanedData;
  };

  const modifyVariables = ({ values }: ModifyVariablesArgs) => {
    const supplier = suppliersFormatter(values);
    return {
      ...supplier,
    };
  };

  const templateUploadConfig = {
    duplicateKey: 'supplierName',
    itemLabel: 'Supplier',
    sampleItemIdentifier: 'Sample Supplier',
    formatter: suppliersFormatter,
  };

  return (
    <>
      <TitleUpdater title="Suppliers" />
      <TitleBar
        title="Data Inputs"
        subtitle="Suppliers"
        brief="Add or remove suppliers below. This allows MEO to map and quantify exposure to the broader supply chain."
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Suppliers"
          columns={columns}
          data={formattedData}
          loading={loadingStates}
          editable
          formFields={formFields}
          createGQL={createSupplier}
          updateGQL={editSupplier}
          deleteGQL={deleteSupplier}
          modifyVariables={modifyVariables}
          // extendActions={extendedActions}
          // templateColumns={templateColumnConfigs}
          // templateUploadConfig={templateUploadConfig}
          // exportExcelColumns={baseColumns}
        />
      </QueryErrorBoundary>
      <MaterialsForm
        isMaterialsModalOpen={isMaterialsModalOpen}
        recordToEdit={recordToEdit}
        onCancel={handleCloseMaterialsForm}
      />
      <BCPFile
        isBCPModalOpen={isBCPModalOpen}
        handleCloseBCPModal={handleCloseBCPModal}
        recordToEdit={recordToEdit}
        onCancel={handleCloseBCPModal}
        acceptedFile={acceptedFile}
        updateAcceptedFile={updateAcceptedFile}
        currentFile={currentFile}
        updateCurrentFile={updateCurrentFile}
      />
      <div className="w-full inline-flex flex-col items-center justify-center mt-14">
        <Footer backPath={'/data-inputs'} />
      </div>
    </>
  );
}
